import PrimaryButton from "@/Components/UI/Forms/PrimaryButton";
import Modal from "@/Components/UI/Modal";
import CoachReviewDisclosure from "@/Components/UI/Reviews/CoachReviewDisclosure";
import CoachReviewImpactRadios from "@/Components/UI/Reviews/CoachReviewImpactRadios";
import {
    ImpactLevel,
    ResponseOption,
    SubmissionSource,
} from "@/Components/UI/Reviews/coachReviewOptions";
import { AnalyticsContext } from "@/Contexts/AnalyticsContext";
import { GiftCoachingContext } from "@/Contexts/GiftCoachingContext";
import { Coach } from "@/types/page";
import { coachWithCerts } from "@/utils/fullUserNames";
import axios from "axios";
import { useContext, useEffect, useState } from "react";
import { FiLoader } from "react-icons/fi";
import TextAreaInput from "../Forms/TextAreaInput";

type CoachReviewModalProps = {
    showModal: boolean;
    onRequestClose: (negativeFeedback: boolean) => void;
    coach: Coach;
    existingReviewId: number | null;
    initialImpactLevel?: ImpactLevel | null;
    submissionSource?: Exclude<SubmissionSource, "quit_flow">;
};

export default function CoachReviewModal({
    showModal,
    onRequestClose,
    coach,
    existingReviewId = null,
    initialImpactLevel = null,
    submissionSource,
}: CoachReviewModalProps) {
    const { showGiftCoachingModal } = useContext(GiftCoachingContext);
    const analytics = useContext(AnalyticsContext);

    const [isProcessing, setIsProcessing] = useState(false);
    const [response, setResponse] = useState<ResponseOption | undefined>(
        initialImpactLevel ?? undefined,
    );
    const [reviewText, setReviewText] = useState("");

    const showImpactSection = !existingReviewId || initialImpactLevel !== null;

    useEffect(() => {
        if (showModal && submissionSource) {
            analytics.trackEvent("coach_review_modal_opened", {
                touchpoint: submissionSource,
                coach_id: coach.id,
            });
        }
    }, [showModal]);

    const closeModal = () => {
        setIsProcessing(false);
        setResponse(initialImpactLevel ?? undefined);
        setReviewText("");
        const feedbackWasNegative =
            response === "negative" || response === "no_impact";
        onRequestClose(feedbackWasNegative);
    };

    const canDoReferrals =
        useContext(GiftCoachingContext).canDoReferrals ?? false;

    const submitReview = async () => {
        setIsProcessing(true);

        if (response === "ask_later") {
            closeModal();
            return;
        }

        if (
            (response === "life_changing" || response === "positive") &&
            canDoReferrals
        ) {
            showGiftCoachingModal();
        }

        if (existingReviewId) {
            try {
                const payload: {
                    text: string;
                    impact_level?: ImpactLevel;
                    source?: SubmissionSource;
                } = {
                    text: reviewText,
                };
                if (showImpactSection && response) {
                    payload.impact_level = response;
                }
                if (submissionSource) {
                    payload.source = submissionSource;
                }
                await axios.patch(
                    route("coachReview.update", existingReviewId),
                    payload,
                );
            } catch (e) {
                closeModal();
                throw e;
            }
            closeModal();
            return;
        }

        try {
            await axios.post(route("coachReview.store"), {
                coach_id: coach.id,
                impact_level: response,
                text: reviewText,
            });
        } catch (e) {
            closeModal();
            throw e;
        }
        closeModal();
    };

    const textAreaPlaceholder = `${existingReviewId === null ? "(Optional) " : ""}E.g. What changed for you after working with your coach? A few words from you can help someone else find the right coach.`;

    return (
        <Modal
            id="coach-review-modal"
            onClose={() => closeModal()}
            show={showModal}
            maxWidth="3xl"
            disableBackgroundClickToClose={true}
            showCloseButton={existingReviewId !== null}
            animateClose={false}
        >
            <div className="max-w-[600px] p-8">
                <div className="flex flex-col items-center">
                    <div className="flex w-full flex-col items-center">
                        {showImpactSection && (
                            <>
                                <div className="mt-2 self-stretch text-lg font-bold mb-2">
                                    What impact has coaching with{" "}
                                    {coachWithCerts(coach)} had so far?
                                </div>
                                <div className="mb-8 flex self-start">
                                    <div className="mx-4">
                                        <CoachReviewImpactRadios
                                            value={response}
                                            onChange={setResponse}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="mb-2 self-stretch text-lg font-bold">
                            Write a quick review for {coachWithCerts(coach)}?
                        </div>
                        <div className="mb-2 self-stretch">
                            <TextAreaInput
                                placeholder={textAreaPlaceholder}
                                value={reviewText}
                                onChange={(e) => setReviewText(e.target.value)}
                                maxLength={1024}
                            />
                        </div>
                        <CoachReviewDisclosure
                            coach={coach}
                            className="mt-2 mb-6 self-stretch"
                        />

                        <PrimaryButton
                            className="justify-center self-stretch"
                            onClick={() => submitReview()}
                            disabled={
                                isProcessing ||
                                (!response && showImpactSection) ||
                                (reviewText.length === 0 &&
                                    existingReviewId !== null &&
                                    !showImpactSection)
                            }
                        >
                            {isProcessing ? (
                                <FiLoader className="animate-spin" size={32} />
                            ) : (
                                "Submit"
                            )}
                        </PrimaryButton>
                    </div>
                </div>
            </div>
        </Modal>
    );
}
